import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { useAsyncEffect } from '@hooks/index'
import { useGlobalDataStore } from '@stores/globalData/globalData.store'
import { endpointForPathname } from '@utils/api-routes'
import { removeTrailing } from '@utils/formatters/string.formatter'
import http from '@utils/http'

import type { MountCheckFn } from '@hooks/useAsyncEffect'

export type PageContextType<D = any> = {
  loading: boolean
  status: PageStatus
  pageData?: D
  refetchPageData: (mountCheckFn?: MountCheckFn) => Promise<D>
}

export enum PageStatus {
  NAVIGATING = 'navigating',
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error',
}

export const PageApiContext = createContext<PageContextType>({
  loading: true,
  status: PageStatus.LOADING,
  pageData: {},
  refetchPageData: async () => null,
})

interface Props {
  children?: React.ReactNode
  initialPageData?: Record<string, any>
}
/**
 * PageApiProvider handles page-specific data loading for both API requests and bootstrapped data.
 * the `pagesData` state is essentially a cache object where the keys are page paths (eg. /music/search ).
 *
 * As the user navigates through the app, this provider will fetch data from the endpoint corresponding to
 * the current page and save it to the pagesData.
 */
export const PageApiProvider: React.FC<Props> = ({ children, initialPageData }: Props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const serverData = useGlobalDataStore((s) => s.serverData)
  const [status, setStatus] = useState(PageStatus.LOADING)

  // See: https://jasonwatmore.com/post/2021/08/27/react-how-to-check-if-a-component-is-mounted-or-unmounted
  const isMounted = useRef(false)

  const pathname = useMemo(() => removeTrailing('/', location.pathname), [location.pathname])
  const [currentPath, setCurrentPath] = useState(pathname)

  const [pageData, setPageData] = useState(initialPageData ?? serverData?.page_props ?? {})

  // See if we are in a temporary state when path has changed but state hasn't been updated yet ("navigating")
  const computedStatus = currentPath !== pathname ? PageStatus.NAVIGATING : status

  /**
   * Used to re-fetch the page data manually
   */
  const refetchPageData = useCallback(
    async (isActive?: MountCheckFn) => {
      const endpointUrl = endpointForPathname(pathname)
      setStatus(PageStatus.LOADING)
      const [resp, errors] = await http.get(endpointUrl, { params: search })

      // Do not try to set state if component unmounts before request finishes
      if (typeof isActive === 'function' && !isActive()) {
        return
      }

      let data
      if (errors) {
        setStatus(PageStatus.ERROR)
        data = { errors }
      } else {
        setStatus(PageStatus.LOADED)
        data = resp?.data ?? null
      }

      setPageData(data)
      return data
    },
    [pathname, search]
  )

  useEffect(() => {
    setCurrentPath(pathname)
  }, [pathname])

  // Fetch API data for this route
  useAsyncEffect(
    async (isActive) => {
      if (isMounted.current) {
        if (typeof location.state === 'object' && (location.state as any)?.shallow) {
          // Remove location state
          navigate(
            {
              pathname: location.pathname,
              search: location.search,
            },
            { replace: true }
          )
          return
        }

        await refetchPageData(isActive)
      } else {
        // no need to fetch if this is the first page we've loaded since we expect
        // bootstrapped data from the server
        isMounted.current = true
        setStatus(PageStatus.LOADED)
      }
    },
    [pathname, search, refetchPageData]
  )

  const contextValue: PageContextType = {
    loading: status === PageStatus.LOADING || status === PageStatus.NAVIGATING,
    status: computedStatus,
    pageData,
    refetchPageData,
  }

  return <PageApiContext.Provider value={contextValue}>{children}</PageApiContext.Provider>
}
export default PageApiProvider

export function usePageApi<D = unknown>(): PageContextType<D> {
  return useContext<PageContextType<D>>(PageApiContext)
}
